import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import Modal from "@/features/shared/components/Modal/Modal";
import ConfirmDeleteUserModal from "@/features/user/components/ConfirmDeleteUserModal";
import UserTableFilters from "@/features/user/components/UserTableFilters";
import UserTable from "@/features/user/components/UserTable";
import UserPagination from "@/features/user/components/UserPagination";
import UserForm from "@/features/user/components/UserForm";
import useUsers from "@/features/user/hooks/useUsers";
import { IoMdAdd } from "react-icons/io";
import { toast } from "react-toastify";
import EmployeeForm from "@/features/employee/components/EmployeeForm";
import { fetchAllContractTypes } from "@/features/contractType/services/contractTypeService";
import { createEmployee } from "@/features/employee/services/employeeService";
import { useQueryClient } from "react-query";
const UserManagementPage = () => {
    const [filters, setFilters] = useState({ first_name: '', last_name: '', email: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formError, setFormError] = useState(null);
    const [deleteUserId, setDeleteUserId] = useState(null);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
    const [isEmployeeModalVisible, setIsEmployeeModalVisible] = useState(false);
    const [selectedEmployeeUser, setSelectedEmployeeUser] = useState(null);
    const [contractTypes, setContractTypes] = useState([]);
    const [updatingManagerId, setUpdatingManagerId] = useState(null);
    const queryClient = useQueryClient();
    // Use the react-query hook which refetches data automatically when page or filters change.
    const { users, meta, loading, error, handleCreateUser, handleUpdateUser, handleDeleteUser, handleResetPassword, handleUpdateManager, refetchUsers, } = useUsers(currentPage, filters, false, undefined, rowsPerPage);
    const { users: managerOptions } = useUsers(undefined, undefined, true, undefined, 1000);
    const handleFilterChange = (filterName, value) => {
        setFilters(prev => ({ ...prev, [filterName]: value }));
        setCurrentPage(1); // Reset to first page on filter change.
    };
    const handleResetFilters = () => {
        setFilters({ first_name: '', last_name: '', email: '' });
    };
    const handleAddUser = () => {
        setSelectedUser(null);
        setIsModalVisible(true);
    };
    const handleEditUser = (user) => {
        setSelectedUser(user);
        setIsModalVisible(true);
    };
    const handleResetPasswordClick = async (userId) => {
        const confirmReset = window.confirm('Réinitialiser le mot de passe de cet utilisateur ?');
        if (!confirmReset)
            return;
        try {
            await handleResetPassword(userId);
            toast.success("Email de réinitialisation envoyé");
        }
        catch (error) {
            toast.error(`Erreur : ${error.message}`);
        }
    };
    const handleDeleteClick = (id) => {
        setDeleteUserId(id);
        setIsDeleteModalVisible(true);
    };
    const handleManagerChange = async (userId, managerId) => {
        setUpdatingManagerId(userId);
        try {
            await handleUpdateManager(userId, managerId);
            toast.success("Manager mis à jour");
        }
        catch (error) {
            toast.error(error.message ?? "Impossible de mettre à jour le manager");
        }
        finally {
            setUpdatingManagerId(null);
        }
    };
    const handleOpenEmployeeModal = (user) => {
        setSelectedEmployeeUser(user);
        setIsEmployeeModalVisible(true);
    };
    const closeEmployeeModal = () => {
        setSelectedEmployeeUser(null);
        setIsEmployeeModalVisible(false);
    };
    const confirmDelete = async () => {
        if (deleteUserId === null)
            return;
        try {
            await handleDeleteUser(deleteUserId);
            toast.success('Utilisateur supprimé');
        }
        catch (error) {
            toast.error(`Erreur : ${error.message}`);
        }
        finally {
            setIsDeleteModalVisible(false);
            setDeleteUserId(null);
        }
    };
    const cancelDelete = () => {
        setIsDeleteModalVisible(false);
        setDeleteUserId(null);
    };
    const handleSubmit = async (idUser, userData) => {
        try {
            setFormError(null);
            if (selectedUser) {
                await handleUpdateUser(idUser, userData);
                toast.success("Utilisateur modifié");
            }
            else {
                await handleCreateUser(userData);
                toast.success("Utilisateur enregistré");
            }
            setIsModalVisible(false);
            // Optionally, refetchUsers() is triggered automatically by React Query.
        }
        catch (error) {
            setFormError(error.message);
            toast.error(`Erreur : ${error.message}`);
        }
    };
    const handleSubmitEmployee = async (payload) => {
        try {
            await createEmployee(payload);
            toast.success("Dossier employé créé");
            await queryClient.invalidateQueries("employees");
            closeEmployeeModal();
        }
        catch (error) {
            toast.error(error.message ?? "Impossible de créer le dossier employé");
        }
    };
    useEffect(() => {
        const loadContractTypes = async () => {
            try {
                const response = await fetchAllContractTypes();
                setContractTypes(response);
            }
            catch (error) {
                toast.error(error.message ?? "Impossible de charger les types de contrat");
            }
        };
        void loadContractTypes();
    }, []);
    if (loading) {
        return _jsx("div", { className: "p-4", children: "Chargement..." });
    }
    if (error) {
        return (_jsxs("div", { className: "p-4 text-red-500", children: ["Erreur lors du chargement des utilisateurs : ", error, _jsx("button", { onClick: () => refetchUsers(), className: "ml-4 bg-blue-500 text-white px-2 py-1 rounded", children: "R\u00E9essayer" })] }));
    }
    return (_jsxs("div", { className: "p-4 bg-gray-50 min-h-screen", children: [_jsxs("div", { className: "flex col-auto gap-4 mb-2", children: [_jsx("h4", { className: "text-xl font-bold", children: "Gestion des Utilisateurs" }), _jsx("button", { onClick: handleAddUser, title: "Ajouter un Utilisateur", className: "rounded", children: _jsx(IoMdAdd, { className: "bg-blue-500 text-white hover:bg-blue-600" }) })] }), _jsx(UserTableFilters, { filters: filters, onFilterChange: handleFilterChange, onResetFilters: handleResetFilters }), _jsx(UserTable, { users: users, managerOptions: managerOptions, onEdit: handleEditUser, onDelete: handleDeleteClick, onResetPassword: handleResetPasswordClick, onCreateEmployee: handleOpenEmployeeModal, onUpdateManager: handleManagerChange, updatingManagerId: updatingManagerId }), meta &&
                _jsx(UserPagination, { currentPage: meta.page ?? 1, totalPages: meta.totalPages ?? 1, limit: rowsPerPage, onPageChange: (page) => setCurrentPage(page), onLimitChange: (value) => {
                        setRowsPerPage(value);
                        setCurrentPage(1);
                    } }), isModalVisible &&
                _jsx(Modal, { onClose: () => setIsModalVisible(false), children: _jsx(UserForm, { initialData: selectedUser ?? undefined, managers: managerOptions, onSubmit: handleSubmit, onCancel: () => setIsModalVisible(false) }) }), isEmployeeModalVisible && selectedEmployeeUser && (_jsx(Modal, { onClose: closeEmployeeModal, children: _jsx(EmployeeForm, { users: users, contractTypes: contractTypes, onSubmit: handleSubmitEmployee, onCancel: closeEmployeeModal, defaultUserId: selectedEmployeeUser.id }) })), isDeleteModalVisible && (_jsx(ConfirmDeleteUserModal, { onConfirm: confirmDelete, onCancel: cancelDelete })), formError && (_jsxs("div", { className: "mt-4 p-3 bg-red-100 text-red-700 rounded", children: ["Erreur : ", formError] }))] }));
};
export default UserManagementPage;
